import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useGallery, { useFiltered } from "@/hooks/useGallery";
import FilterBar from "@/components/FilterBar";
import GalleryGrid from "@/components/GalleryGrid";
import Lightbox from "@/components/Lightbox";
import Seo from "@/components/Seo";
import HeroVideoLoop from "@/components/HeroVideoLoop";
import { resolveMedia } from "@/lib/api";
import { videoThumbUrl } from "@/lib/video";
import { ArrowRight, Plane, Camera } from "lucide-react";

const HOME_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "HighwaysLens",
  url: "https://highwayslens.co.uk",
  logo: "https://highwayslens.co.uk/logo.png",
  description:
    "HighwaysLens is a premium creative production studio specialising in highways, infrastructure and construction photography, drone and aerial imaging, and video production across the UK.",
  address: {
    "@type": "PostalAddress",
    addressCountry: "GB",
  },
};

const coverFor = (list) => {
  const pick = list.find((i) => i.featured && (i.thumbnail || i.image)) || list.find((i) => i.thumbnail || i.image) || list[0];
  if (!pick) return "";
  return resolveMedia(pick.webp_thumbnail || pick.thumbnail || pick.webp_image || pick.image) || videoThumbUrl(pick);
};

function ShotTypePanel({ to, testid, icon: Icon, eyebrow, title, count, cover }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group relative block h-[200px] sm:h-[260px] overflow-hidden bg-[#0a0a0a] border border-[#1a1a1a] hover:border-[#4A6D8C]/60 transition-colors"
    >
      {cover && (
        <img
          src={cover}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60 transition-transform duration-[1200ms] ease-out group-hover:scale-105 group-hover:opacity-75"
          loading="lazy"
        />
      )}
      <span className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      <div className="absolute left-6 right-6 bottom-6">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#7BA8CC] mb-2">
          <Icon size={12} /> {eyebrow}
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight">{title}</h2>
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-[#A3A3A3]">{count} item{count !== 1 ? "s" : ""}</span>
          <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-white/80 group-hover:text-white transition-colors">
            Explore <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const { items, loading } = useGallery();
  const [filter, setFilter] = useState("All");
  const [activeId, setActiveId] = useState(null);
  const filtered = useFiltered(items, filter);
  const aerial = useMemo(() => items.filter((i) => i.shot_type === "aerial"), [items]);
  const ground = useMemo(() => items.filter((i) => i.shot_type !== "aerial"), [items]);

  return (
    <div className="pb-16">
      <Seo
        title="Highways Lens | Highways Photography, Drone & Video"
        description="Highways Lens is the UK's specialist creative production partner for highways and infrastructure, striking photography, drone and film. View our work."
        path="/"
        jsonLd={HOME_JSON_LD}
      />
      {/* Hero with looping background video */}
      <section
        className="relative isolate min-h-[70vh] sm:min-h-[80vh] flex items-end px-5 sm:px-8 lg:px-10 pt-24 pb-14 lg:pb-20 overflow-hidden"
        data-testid="home-hero"
      >
        <HeroVideoLoop />
        <div className="relative max-w-4xl">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#7BA8CC] mb-5">
            HighwaysLens · Studio
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.7)]">
            HighwaysLens | Highways <br className="hidden sm:block" />
            Photography, Drone <br className="hidden sm:block" />
            &amp; Video Production
          </h1>
          <p className="text-[15px] text-[#d9dde2] mt-7 leading-relaxed max-w-2xl drop-shadow-[0_1px_8px_rgba(0,0,0,0.55)]">
            HighwaysLens captures the people, projects, plant, places, and progress
            behind the UK highways sector, from live night works and structures
            to depots, drone surveys, and project films.
          </p>
        </div>
      </section>

      {/* Aerial / Ground entrance panels */}
      {!loading && items.length > 0 && (
        <section className="px-5 sm:px-8 lg:px-10 pt-8" data-testid="home-shot-type-panels">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ShotTypePanel
              to="/aerial"
              testid="home-panel-aerial"
              icon={Plane}
              eyebrow="From the air"
              title="Aerial & Drone"
              count={aerial.length}
              cover={coverFor(aerial)}
            />
            <ShotTypePanel
              to="/ground"
              testid="home-panel-ground"
              icon={Camera}
              eyebrow="On site"
              title="Ground & On-Site"
              count={ground.length}
              cover={coverFor(ground)}
            />
          </div>
        </section>
      )}

      <div className="px-5 sm:px-8 lg:px-10 pt-10 lg:pt-12">
        <h2 className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-5">
          Our Work
        </h2>
        {/* Filter */}
        <div className="mb-7 lg:mb-9 overflow-x-auto -mx-2 px-2">
          <FilterBar active={filter} onChange={setFilter} />
        </div>

        {/* Grid */}
        {loading ? (
          <div
            className="text-[#737373] text-xs tracking-[0.24em] uppercase py-20 text-center"
            data-testid="gallery-loading"
          >
            Loading gallery…
          </div>
        ) : (
          <GalleryGrid items={filtered} onOpen={setActiveId} />
        )}
      </div>

      {activeId !== null && (
        <Lightbox
          items={filtered}
          activeId={activeId}
          onClose={() => setActiveId(null)}
          onChange={setActiveId}
        />
      )}
    </div>
  );
}
